import { type ComponentProps, useMemo } from 'react'

import { ButtonRadioGroup } from '@/components/common/Button'
import { InputSelect, InputSelectWithLabel, InputWithLabel } from '@/components/common/Input'
import { Icon } from '@/components/icons/Icon'
import { IconWithLabel } from '@/components/icons/IconWithLabel'
import { GALLERY_VIEW_LABELS, GALLERY_VIEW_SLUGS, type GalleryViewSlugType } from '@/defs/gallery'
import { galleryActions } from '@/store/gallery'
import { useReduxDispatch } from '@/store/hooks'
import { cn } from '@/utils/commonUtil'
import { scrollToAnchor } from '@/utils/scrollUtil'

import { useGalleryDisplay } from '../../GalleryDisplayProvider/GalleryDisplayProvider'
import { useGalleryBounce } from '../../hooks/useGalleryBounce'

import styles from './GalleryControlsView.module.scss'

type Props = {
  variant?: ComponentProps<typeof InputWithLabel>['variant']
}

const OPTIONS = GALLERY_VIEW_SLUGS.map((slug) => ({
  label: <IconWithLabel iconBefore={GALLERY_VIEW_LABELS[slug].icon}>{GALLERY_VIEW_LABELS[slug].label}</IconWithLabel>,
  value: slug,
}))

export const GalleryControlsView = ({ variant }: Props) => {
  const dispatch = useReduxDispatch()
  const { view } = useGalleryDisplay()
  const isBouncing = useGalleryBounce({ editedKeys: ['view'] })

  const onSelect = (viewNew: GalleryViewSlugType) => {
    if (viewNew !== view)
      requestAnimationFrame(() => scrollToAnchor({ id: 'gallery', ifNearTop: true, isSmooth: true }))
    dispatch(galleryActions.setView(viewNew))
  }

  const options = useMemo(
    () =>
      GALLERY_VIEW_SLUGS.map((slug) => ({
        ariaLabel: GALLERY_VIEW_LABELS[slug].label,
        isSelected: slug === view,
        label: <Icon name={GALLERY_VIEW_LABELS[slug].icon} />,
        popover: (
          <IconWithLabel iconBefore={GALLERY_VIEW_LABELS[slug].icon}>{GALLERY_VIEW_LABELS[slug].label}</IconWithLabel>
        ),
        value: slug,
      })),
    [view],
  )

  return (
    <div className={cn(styles.container, { isBouncing, styles })}>
      {variant === 'horizontal' ? (
        <InputSelect onSelect={onSelect} options={OPTIONS} size='sm' value={view}>
          <InputSelectWithLabel label='View' options={OPTIONS} size='sm' value={view} variant={variant} />
        </InputSelect>
      ) : (
        <InputWithLabel label='View' variant={variant}>
          <ButtonRadioGroup
            as='button'
            onSelect={onSelect}
            options={options}
            shadow='none'
            size='sm'
            textStyle='none'
          />
        </InputWithLabel>
      )}
    </div>
  )
}
